/* ─────────────────────────────────────────────────────────────
   ELO UTILITY — calculateElo() and calculateReliability()

   Calculates the rating change for both players after a match.
   Imports all constants from eloConfig.js (single source of truth).

   Usage:
     import { calculateElo, calculateReliability } from '../utils/elo';

     const result = calculateElo({
       winnerRating:   1350,
       loserRating:    1200,
       winnerMatches:  45,
       loserMatches:   12,
       format:         'bo5',   // 'bo1' | 'bo3' | 'bo5' | 'bo7'
       winnerGames:    3,
       loserGames:     1,
     });
     // result = { winnerChange: +12, loserChange: -18,
     //            newWinnerRating: 1362, newLoserRating: 1182 }

     const reliability = calculateReliability(matchesPlayed, uniqueOpponents);
     // reliability = 0–100 (integer)
   ───────────────────────────────────────────────────────────── */

import { eloConfig } from '../config/eloConfig';

const {
  minimumRating,
  kBase,
  kDecay,
  kDecayRate,
  marginMultipliers,
} = eloConfig;

/* ── Helper: smooth decay K-factor ────────────────────────────
   K = floor(kBase + (kDecay / (1 + n / kDecayRate)))
   New players start near K=64 for fast placement and
   asymptotically approach K=32 as match count grows.
   No hard thresholds — no abrupt jumps. */
function getKFactor(matchesPlayed) {
  return Math.floor(kBase + (kDecay / (1 + matchesPlayed / kDecayRate)));
}

/* ── Helper: look up the margin of victory multiplier ─────────
   Builds a lookup key from the format and game scores, e.g.
   "bo5_3_1", then returns the matching multiplier.
   Falls back to 1.00 if the combo isn't found. */
function getMultiplier(format, winnerGames, loserGames) {
  if (format === 'bo1') return marginMultipliers.bo1.default;
  const key = `${format}_${winnerGames}_${loserGames}`;
  return marginMultipliers[key] ?? 1.00;
}

/* ── Main export: calculateElo ─────────────────────────────────
   Takes one object with all the match details and returns the
   rating changes and new ratings for both players. */
export function calculateElo({
  winnerRating,
  loserRating,
  winnerMatches,
  loserMatches,
  format,
  winnerGames,
  loserGames,
}) {
  // Step 1 — Expected scores (probability of winning, 0 to 1)
  // A higher-rated player has an expected score closer to 1.
  const winnerExpected = 1 / (1 + Math.pow(10, (loserRating  - winnerRating) / 400));
  const loserExpected  = 1 / (1 + Math.pow(10, (winnerRating - loserRating)  / 400));

  // Step 2 — K-factors (may differ between the two players)
  const winnerK = getKFactor(winnerMatches);
  const loserK  = getKFactor(loserMatches);

  // Step 3 — Margin of victory multiplier
  const multiplier = getMultiplier(format, winnerGames, loserGames);

  // Step 4 — Rating changes
  // Actual score: winner = 1, loser = 0
  // Formula: K * multiplier * (actualScore - expectedScore)
  const winnerChange = Math.round((winnerK * multiplier) * (1 - winnerExpected));
  const loserChange  = Math.round((loserK  * multiplier) * (0 - loserExpected));

  // Step 5 — New ratings, clamped to the rating floor
  const newWinnerRating = Math.max(minimumRating, winnerRating + winnerChange);
  const newLoserRating  = Math.max(minimumRating, loserRating  + loserChange);

  return {
    winnerChange,      // e.g. +18  (always positive)
    loserChange,       // e.g. -14  (always negative or zero)
    newWinnerRating,   // e.g. 1368
    newLoserRating,    // e.g. 1186
  };
}

/* ── Export: calculateReliability ─────────────────────────────
   Reliability is a 0–100 score that reflects how trustworthy
   a player's ELO is. It rewards:
     - Playing more matches (+1 per match, base is 5)
     - Playing a variety of opponents (+5 per unique opponent)

   Formula: min(100, 5 + matchesPlayed + uniqueOpponents × 5)

   A brand new player starts at 5. Playing 10 matches against
   5 different opponents gets them to 5 + 10 + 25 = 40. */
export function calculateReliability(matchesPlayed = 0, uniqueOpponents = 0) {
  return Math.min(100, 5 + matchesPlayed + uniqueOpponents * 5);
}
